/**
 * External link to the bakery's Uber Eats store (delivery).
 *
 * For co-marketing graphics that match Uber's guidelines, merchants can download
 * official badges from Uber (e.g. CTA / "Order on" assets via merchants.ubereats.com
 * resources → z.uber.com/cta-logo-badges) and swap this component for an <img> if desired.
 * This implementation uses approved-style colors and clear "Uber Eats" nominative text only.
 */

import { TruckIcon } from '@heroicons/react/24/outline';
import { UBER_EATS_STORE_URL } from '../constants/ubereats';

/** Uber Eats primary green (widely used for partner CTAs; aligns with brand kit). */
const UBER_EATS_GREEN = '#06C167';

type Variant = 'hero' | 'cta' | 'footer' | 'banner' | 'compact';

export interface UberEatsOrderLinkProps {
  variant?: Variant;
  className?: string;
  /** Override href (tests only; default is production store URL). */
  href?: string;
}

export default function UberEatsOrderLink({
  variant = 'banner',
  className = '',
  href = UBER_EATS_STORE_URL,
}: UberEatsOrderLinkProps) {
  const label = 'Order on Uber Eats';

  if (variant === 'compact') {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center gap-1.5 font-semibold underline decoration-2 underline-offset-2 hover:opacity-90 ${className}`}
        style={{ color: UBER_EATS_GREEN }}
        data-testid="ubereats-order-link"
      >
        <TruckIcon className="h-4 w-4 shrink-0" aria-hidden />
        {label}
      </a>
    );
  }

  if (variant === 'footer') {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors ${className}`}
        data-testid="ubereats-order-link"
      >
        <TruckIcon className="h-5 w-5 shrink-0 text-[#06C167]" aria-hidden />
        <span>{label}</span>
        <span className="text-xs text-gray-500">(delivery)</span>
      </a>
    );
  }

  const pillClasses =
    variant === 'hero'
      ? 'px-5 py-3 text-base shadow-lg hover:shadow-xl'
      : variant === 'cta'
        ? 'px-5 py-3 text-base shadow-md'
        : 'px-4 py-2.5 text-sm sm:text-base';

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98] ${pillClasses} ${className}`}
      style={{ backgroundColor: UBER_EATS_GREEN }}
      data-testid="ubereats-order-link"
    >
      <TruckIcon className="h-5 w-5 shrink-0" aria-hidden />
      {label}
    </a>
  );
}
